import { useRef, useEffect, useState } from "react";
import Button from "../../../components/form-controls/Button";
import Input from "../../../components/form-controls/Input";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import Tags from "../../../components/Tags";
import Select from 'react-select'
import InvalidFeedback from "../../../components/InvalidFeedback";
import FieldForm from "./FieldForm";
import PreviewField from "./PreviewField";
import { FormService } from "../../../services/FormsService";
import { AlertService } from "../../../services/Alert";
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import useModal from "../../../hooks/useModal";
import { useTranslation } from 'react-i18next';
import { MetaService } from "../../../services/MetaService";
import { watch } from "fs";
const expressionToURL = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})|(http:\/\/localhost)./gi;

interface FormModalProps {
    show: boolean;
    onRequestClose: (reload: boolean) => void;
    data?: any;
}
function FormModal({ show, onRequestClose, data }: FormModalProps) {
    console.log(data);
    const { t } = useTranslation();

    const { account } = useCurrentAccount();

    const modalSubRef = useRef<HTMLDivElement>(null);

    useModal(modalSubRef, show)

    const triggerList = [
        { value: 'quiz', label: 'Encuestas' }
    ];
    const [templatesList, setTemplatesList] = useState([]);
    const [emptyList, setEmptyList] = useState(false);

    const defaultValueKeys = {
        type: 'text',
        name: '',
        options: [],
        mandatory: false,

        dateConfig: {
            isLimited: false,
            min: 1,
            max: 1
        }
    }

    const defaultValues = {
        form_id: 0,
        name: '',
        categories: [],
        webhook: '',
        triggers: false,
        type: '',
        trigger_template: 0,
        template_id: '',
        keys: []
    }

    const methods = useForm({
        mode: 'onChange',
        defaultValues
    });

    const { fields: keys, append, remove } = useFieldArray({
        control: methods.control,
        name: "keys",
    });

    const trigger = methods.watch('triggers');
    const typeTrigger = methods.watch('type');

    const submit = async (data) => {
        try {
            data.account_id = account.id
            data.categories = data.categories.join(',')
            data.keys = data.keys.map(k => {
                k.mandatory = k.mandatory ? 1 : 0;
                k.options = k.options ? k.options.join(',') : ''
                return k;
            })

            if (data.triggers) {
                data.trigger_template = 1;
            } else {
                data.template_id = '';
                data.trigger_template = 0;
            }

            const { triggers, type, ...payload } = data;

            if (data.form_id) {
                await FormService.update(payload);
            } else {
                delete data.form_id;
                await FormService.save(payload);
            }

            AlertService.success(t("config.forms.success-submit-title"), t("config.forms.success-submit-sub"));
            await closeAndReset(true)
        } catch (error) {
            console.error(error);
            AlertService.error(t("config.forms.error-submit-title"), t("config.forms.error-submit-sub"))
        }
    }

    const getTemplate = async () => {
        try {

            const { data } = await MetaService.getTemplatesTriggers(account.id, 'APPROVED', typeTrigger)
            if (!data || data.length === 0) {
                setTemplatesList([]);
                setEmptyList(true);
            } else {
                setTemplatesList(data.map(row => ({
                    value: row.id,
                    label: `${row.name}`,
                    data: row
                })));
                setEmptyList(false);
            }

        } catch (error) {
            console.error(error);
            AlertService.error(t("config.forms.error-submit-title"), t("config.forms.error-submit-getTemplates-sub"))
        }
    };

    const closeAndReset = async (reload: boolean) => {
        await onRequestClose(reload);
        methods.reset(defaultValues);
    }

    const addField = () => {
        append(defaultValueKeys)
    }

    useEffect(() => {
        if (data) {
            methods.reset({
                ...defaultValues,
                ...data,
                triggers: data.trigger_template === 1,
                type: data.trigger_template === 1 ? 'quiz' : '',
                template_id: data.template_id,
            });
        }
    }, [data]);

    useEffect(() => {
        if (data && templatesList.length > 0 && data.template_id) {
            const exists = templatesList.find(t => t.value === data.template_id);
            if (exists) {
                methods.setValue('template_id', data.template_id);
            }
        }
    }, [templatesList, data]);

    useEffect(() => {

        if (!trigger) {
            methods.setValue('type', '');
            methods.setValue('template_id', '');
        }

    }, [trigger]);

    useEffect(() => {
        if (typeTrigger) {
            getTemplate();
        }
    }, [typeTrigger]);

    return (
        <FormProvider {...methods}>
            <div className="modal fade" ref={modalSubRef} data-bs-backdrop="static" data-bs-keyboard="false">
                <div className="modal-dialog _modal-dialog-scrollable_ modal-xl" role="document">
                    <form onSubmit={methods.handleSubmit(submit)}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{t("config.forms.form-modal.title")}</h4>
                                <button type="button" className="btn-close" onClick={() => closeAndReset(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-7">
                                        <div className="mb-3">
                                            <Input label={t("config.forms.form-modal.name-title")} errors={methods.formState.errors.name} {...methods.register('name', { required: true })} />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.category")}</label>
                                            <Controller
                                                control={methods.control}
                                                name='categories'
                                                rules={{ required: true }}
                                                render={({
                                                    field: { onChange, value },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <Tags
                                                            isInvalid={error ? true : false}
                                                            onChange={(values: any) => onChange(values)}
                                                            initialValue={value.map(v => ({ value: v, label: v }))}
                                                        />
                                                    )
                                                }}
                                            />
                                            <InvalidFeedback error={methods.formState.errors.categories} />
                                        </div>

                                        <hr />
                                        <div className="mb-3">
                                            <label className="form-label mb-0" >WebHook <span className="form-label-secondary">({t("optional-title")})</span></label>
                                            <p className="form-text mt-1 mb-2">
                                                {t("config.forms.form-modal.webhook-text")}
                                            </p>
                                            <Input label='URL' errors={methods.formState.errors.webhook} {...methods.register('webhook', { pattern: { value: new RegExp(expressionToURL), message: 'Debe ingresar una URL válida' } })} />
                                        </div>

                                        <hr />

                                        <div className="list-group list-group-lg list-group-flush list-group-no-gutters">
                                            <div className="list-group-item">
                                                <div className="d-flex">
                                                    <div className="flex-grow-1">
                                                        <div className="row align-items-center">
                                                            <div className="col">
                                                                <h4 className="mb-0">{t("config.forms.form-modal.triggers-title")} <span className="form-label-secondary">({t("optional-title")})</span></h4>
                                                                <p className="fs-5 text-body mb-0">{t("config.forms.form-modal.triggers-sub")}</p>
                                                            </div>
                                                            <div className="col-auto">
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        id="connectedAccounts1"
                                                                        type="checkbox"
                                                                        {...methods.register('triggers')}
                                                                    />
                                                                    <label
                                                                        className="form-check-label"
                                                                        htmlFor="connectedAccounts1"
                                                                    /> {t("config.forms.form-modal.triggers-switch")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {trigger && (
                                            <div className="row mb-6">
                                                <div className="col-md-6">
                                                    <label className="form-label">
                                                        {t("config.forms.form-modal.triggers-type")} <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={methods.control}
                                                        name="type"
                                                        rules={{ required: true }}
                                                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                                                            const selectedOption = triggerList.find(option => option.value === value) || null;

                                                            return (
                                                                <Select
                                                                    options={triggerList}
                                                                    value={selectedOption}
                                                                    className={error ? "form-select-custom is-invalid" : ""}
                                                                    classNamePrefix="form-select-custom"
                                                                    placeholder={t("config.forms.form-modal.triggers-type-pl")}
                                                                    onChange={(option) => onChange(option?.value)}
                                                                />
                                                            );
                                                        }}
                                                    />

                                                </div>

                                                <div className="col-md-6">
                                                    {typeTrigger === "quiz" && (
                                                        <>
                                                            <label className="form-label">
                                                                {t("config.forms.form-modal.triggers-quiz")} <span className="text-danger">*</span>
                                                            </label>
                                                            <Controller
                                                                control={methods.control}
                                                                name="template_id"
                                                                rules={{ required: true }}
                                                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                                    <Select
                                                                        options={templatesList}
                                                                        className={error ? "form-select-custom is-invalid" : ""}
                                                                        classNamePrefix="form-select-custom"
                                                                        placeholder={t("config.forms.form-modal.triggers-quiz-pl")}
                                                                        value={templatesList.find(option => option.value === value) || null}
                                                                        onChange={(option) => onChange(option?.value)}
                                                                    />
                                                                )}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}


                                        {emptyList && (
                                            <div className="alert alert-warning" role="alert">
                                                <div className="d-flex">
                                                    <div className="flex-shrink-0">
                                                        <i className="bi-exclamation-triangle-fill" />
                                                    </div>
                                                    <div className="flex-grow-1 ms-2"><strong>{t("config.forms.form-modal.empty-alert-strong")}</strong> {t("config.forms.form-modal.empty-alert")}</div>
                                                </div>
                                            </div>
                                        )}



                                        <hr />

                                        <div className="mb-3">
                                            <label className="form-label mb-0" >{t("config.forms.form-modal.field-form-title")} <span className="form-label-secondary">({t("optional-title")})</span></label>
                                            <p className="form-text mt-1 mb-2">
                                                {t("config.forms.form-modal.field-form-text")}
                                            </p>
                                        </div>

                                        <hr />

                                        {
                                            keys.map((k, i) => (
                                                <FieldForm
                                                    key={k.id}
                                                    index={i}
                                                    delete={() => remove(i)}
                                                />
                                            ))
                                        }

                                        <div>
                                            <button type="button" className="btn btn-link px-0" onClick={addField}><i className="bi-plus"></i> {t("config.forms.form-modal.add-field")}</button>
                                        </div>
                                    </div>
                                    <div className="col-5 bg-light p-3 rounded shadow-sm border">
                                        <div className="">
                                            <h4>{t("config.forms.form-modal.side-preview.preview-title")}</h4>
                                            <p>{t("config.forms.form-modal.side-preview.preview-text")}</p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.side-preview.form-label")}</label>
                                            <select className="form-control">
                                                <option>{methods.watch('name') || `${t("config.forms.form-modal.side-preview.empty-name-from")}`}</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.side-preview.customer-name")}</label>
                                            <input type="text" className="form-control gb-white-force" readOnly defaultValue="John Doe" />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label" >Folio *</label>
                                            <input type="text" className="form-control gb-white-force" readOnly defaultValue="12345" />
                                        </div>

                                        {
                                            methods.watch('keys').map((k, i) => <PreviewField key={i} {...k} />)
                                        }

                                        <div className="mb-3">
                                            <label className="form-label text-nowrap mb-0" >{t("config.forms.form-modal.side-preview.status")}</label>
                                            <select className="form-control">
                                                {methods.watch('categories').length === 0 && <option >{t("config.forms.form-modal.side-preview.empty-category")}</option>}
                                                {methods.watch('categories').map((c, i) => <option key={i}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-white btn-sm" onClick={() => closeAndReset(false)}>{t("btn-cancel")}</button>
                                <Button type="submit" className="btn btn-primary btn-sm" disabled={!methods.formState.isValid} isLoading={methods.formState.isSubmitting}>{t("btn-save")}</Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </FormProvider>
    )
}
export default FormModal